import axios from 'axios';
import { action, makeObservable, observable, runInAction, when } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';
import { AsyncDataSource, TableState } from '@servicetitan/table';
import { DataResult, State } from '@servicetitan/data-query';

import { LoadStatus } from '../../../common/enums/load-status';
import { TABLE_PAGE_SIZE } from '../../utils/ux';
import { TelecomApi, OutboundMessage, MessageStatus } from '../api/telecom.api';
import { ManageTenantStore } from '../../stores/manage-tenant.store';

@injectable()
export class OutboundMessagesStore {
    @observable loadStatus = LoadStatus.None;
    @observable tableState = new TableState<OutboundMessage, number>({
        pageSize: TABLE_PAGE_SIZE,
        rowIdKey: 'id',
    });
    @observable initialised = false;

    @observable searchQuery = '';
    @observable statusFilter: MessageStatus[] = [];

    abortController?: AbortController;

    constructor(
        @inject(TelecomApi) private telecomApi: TelecomApi,
        @inject(ManageTenantStore) private manageTenantStore: ManageTenantStore,
    ) {
        makeObservable(this);
    }

    @action init = () => {
        if (!this.initialised) {
            this.initialised = true;

            this.tableState.setDataSource(
                new AsyncDataSource(
                    { get: this.fetchOutboundMessages },
                    (row: OutboundMessage) => row.id,
                ),
            );
        }
    };

    // Fetch data for AsyncDataSource - clean API call like tenants store
    fetchOutboundMessages = async (state: State): Promise<DataResult<OutboundMessage>> => {
        const searchQuery = this.searchQuery;
        const statusFilter = this.statusFilter.length > 0 ? this.statusFilter : undefined;

        this.abortController?.abort();
        await when(() => this.loadStatus !== LoadStatus.Loading);
        this.abortController = new AbortController();

        const prevLoadStatus = this.loadStatus;

        if (!this.manageTenantStore.tenant) {
            return {
                data: [],
                total: 0,
            };
        }

        try {
            runInAction(() => {
                this.loadStatus = LoadStatus.Loading;
            });

            const skip = state.skip ?? 0;
            const page = Math.floor(skip / TABLE_PAGE_SIZE) + 1;

            let sortField: string | undefined;
            let isAscending: boolean | undefined;

            if (state.sort?.[0]) {
                const field = state.sort[0].field;
                // Convert to PascalCase for backend API
                sortField = field.charAt(0).toUpperCase() + field.slice(1);
                isAscending = state.sort[0].dir === 'asc';
            }

            const response = await this.telecomApi.outboundMessages(
                page,
                state.take,
                isAscending,
                sortField,
                searchQuery || undefined,
                statusFilter && statusFilter.length > 0 ? statusFilter : undefined,
                undefined,
                undefined, // @TODO - fix dateFrom using date picker
                undefined, // @TODO - fix dateTo using date picker
                this.manageTenantStore.tenant!.internalId,
                this.manageTenantStore.tenant!.partnerId,
                this.abortController?.signal,
            );

            runInAction(() => {
                this.loadStatus = LoadStatus.Ok;
            });

            return {
                data: response.data?.items || [],
                total: response.data?.count || 0,
            };
        } catch (error) {
            if (axios.isCancel(error)) {
                runInAction(() => {
                    this.loadStatus = prevLoadStatus;
                });

                return {
                    data: this.tableState.data,
                    total: this.tableState.totalCount,
                };
            }

            runInAction(() => {
                this.loadStatus = LoadStatus.Error;
            });

            return {
                data: [],
                total: 0,
            };
        }
    };

    // Search functionality
    filterBySearchQuery = (searchQuery: string) => {
        runInAction(() => {
            this.searchQuery = searchQuery;
        });

        this.fetchTableDataFromStart();
    };

    filterByStatus = (statusFilter: MessageStatus[]) => {
        runInAction(() => {
            this.statusFilter = statusFilter;
        });

        this.fetchTableDataFromStart();
    };

    clearFilters = () => {
        runInAction(() => {
            this.searchQuery = '';
            this.statusFilter = [];
        });

        this.fetchTableDataFromStart();
    };

    fetchTableDataFromStart = () => this.tableState.fetchData({ newSkip: 0 });

    get isLoading() {
        return this.loadStatus === LoadStatus.Loading;
    }

    get hasError() {
        return this.loadStatus === LoadStatus.Error;
    }
}
